import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence, MotionConfig } from 'framer-motion';

import Navbar from './components/Navbar';
import Footer from './components/Footer';
import PageTransition from './components/PageTransition';
import WhatsAppFloat from './components/WhatsAppFloat';
import AIChat from './components/AIChat';
import ScrollProgress from './components/ScrollProgress';
import SmoothScroll, { scrollToTop } from './motion/SmoothScroll';
import Cursor from './motion/Cursor';
import Preloader from './motion/Preloader';

import { AdminAuthProvider, useAdminAuth } from './admin/context/AdminAuthContext';
import AdminLayout from './admin/components/AdminLayout';

import Welcome from './pages/Welcome';
import Leadership from './pages/Leadership';
import Capabilities from './pages/Capabilities';
import Portfolio from './pages/Portfolio';
import RequestConsultation from './pages/RequestConsultation';
import NotFound from './pages/NotFound';

const AdminLogin = lazy(() => import('./admin/pages/AdminLogin'));
const AdminDashboard = lazy(() => import('./admin/pages/AdminDashboard'));
const AdminEnquiries = lazy(() => import('./admin/pages/AdminEnquiries'));
const AdminProjects = lazy(() => import('./admin/pages/AdminProjects'));
const AdminOfficeInfo = lazy(() => import('./admin/pages/AdminOfficeInfo'));
const AdminSettings = lazy(() => import('./admin/pages/AdminSettings'));

function PageLoader() {
  return (
    <div className="page-loader">
      <div className="page-loader-inner"><div className="ring" /><span>Blue Block PMC</span></div>
    </div>
  );
}

function RequireAdmin({ children }) {
  const { isLoggedIn } = useAdminAuth();
  const location = useLocation();
  if (!isLoggedIn) return <Navigate to="/admin/login" state={{ from: location }} replace />;
  return <AdminLayout>{children}</AdminLayout>;
}

function PublicShell() {
  const location = useLocation();
  return (
    <MotionConfig reducedMotion="user">
      <Preloader />
      <SmoothScroll />
      <Cursor />
      <ScrollProgress />
      <Navbar />
      <AnimatePresence mode="wait" onExitComplete={() => scrollToTop(true)}>
        <PageTransition key={location.pathname}>
          <Routes location={location}>
            <Route path="/" element={<Welcome />} />
            <Route path="/leadership" element={<Leadership />} />
            <Route path="/services" element={<Capabilities />} />
            <Route path="/capabilities" element={<Navigate to="/services" replace />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/request-consultation" element={<RequestConsultation />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </PageTransition>
      </AnimatePresence>
      <Footer />
      <AIChat />
      <WhatsAppFloat />
    </MotionConfig>
  );
}

function AdminShell() {
  return (
    <AdminAuthProvider>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
          <Route path="/admin/enquiries" element={<RequireAdmin><AdminEnquiries /></RequireAdmin>} />
          <Route path="/admin/projects" element={<RequireAdmin><AdminProjects /></RequireAdmin>} />
          <Route path="/admin/office" element={<RequireAdmin><AdminOfficeInfo /></RequireAdmin>} />
          <Route path="/admin/settings" element={<RequireAdmin><AdminSettings /></RequireAdmin>} />
          <Route path="/admin/*" element={<Navigate to="/admin/dashboard" replace />} />
        </Routes>
      </Suspense>
    </AdminAuthProvider>
  );
}

function AppShell() {
  const { pathname } = useLocation();
  return pathname.startsWith('/admin') ? <AdminShell /> : <PublicShell />;
}

export default function App() {
  return (
    <Router>
      <AppShell />
    </Router>
  );
}